import { AfterContentChecked, AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { map, startWith } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { NotAvailableReason, NotAvailableReasonRequest } from '../../models/not-available-reason';
import { LoggingService } from '../../services/logging.service';
import { NotAvailableReasonService } from '../../services/not-available-reason.service';
import { SettingsService } from '../../services/settings.service';
import { RoutingService } from '../../services/routing.service';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'app-not-available-reason',
  templateUrl: './not-available-reason.component.html',
  styleUrls: ['./not-available-reason.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotAvailableReasonComponent implements OnInit, AfterViewInit, AfterContentChecked {
  @ViewChild('narButtonFields')
  private narButtonFields: ElementRef;

  @ViewChild('auto', { static: true })
  public autocomplete: ElementRef<MatAutocomplete>;

  public icons = ICONS;

  public TranslationKeys = TranslationKeys;

  public selectedRequest: NotAvailableReasonRequest;
  public openRequests: NotAvailableReasonRequest[] = [];

  public notAvailableReasons: NotAvailableReason[] = [];
  public dropdownOptions: NotAvailableReason[] = [];
  public filteredOptions: Observable<NotAvailableReason[]>;

  public selectedOption: NotAvailableReason;

  public form!: UntypedFormGroup;

  public control: UntypedFormControl = new UntypedFormControl();
  public isProcessingNar: boolean;

  public constructor(
    private formBuilder: UntypedFormBuilder,
    private readonly narService: NotAvailableReasonService,
    private readonly settingsService: SettingsService,
    private readonly translateService: TranslateService,
    private readonly loggingService: LoggingService,
    private readonly routingService: RoutingService,
  ) {
    this.settingsService.settings$.pipe(untilDestroyed(this)).subscribe((settings) => {
      this.notAvailableReasons = settings.notAvailableReasons;

      if (this.narButtonFields) {
        this.updateDropdown();
      }
    });

    this.narService.openRequests$.pipe(untilDestroyed(this)).subscribe((openRequests) => {
      if (!openRequests || openRequests.length < 1) {
        this.redirectHome();
      } else {
        this.openRequests = openRequests;
        this.selectedRequest = openRequests[0];
      }
    });
  }

  public ngOnInit(): void {
    this.form = this.formBuilder.group({
      dropdownOptions: new FormControl([this.dropdownOptions[0], Validators.required]),
    });
    this.filteredOptions = of(this.dropdownOptions);
  }

  public ngAfterViewInit(): void {
    this.updateDropdown();
  }

  public ngAfterContentChecked(): void {
    if (this.narButtonFields) {
      this.updateDropdown();
    }
    if (this.dropdownOptions.length > 0) {
      this.control.enable();
    } else {
      this.control.disable();
    }
  }

  public cancel(): void {
    this.narService.cancel();
    this.redirectHome();
  }

  public onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    this.selectedOption = this.notAvailableReasons.find((x) => x.reasonId === event.option.id);
  }

  public selectNar(reasonId: string): void {
    this.selectedOption = this.notAvailableReasons.find((x) => x.reasonId === reasonId);
    this.submit();
    this.control.setValue(null);
  }

  public updateDate(startTime: number): string {
    if (moment(startTime).isSame(new Date(), 'day')) {
      return moment(startTime)
        .format(`[${this.translateService.instant(TranslationKeys.GeneralToday)}], LTS`)
        .toString();
    }

    return moment(startTime).format('DD.MM.YYYY, LTS').toString();
  }

  public onClearValueClick(): void {
    this.control.setValue('');
    this.selectedOption = null;
  }

  public submit(): void {
    this.loggingService.Info(
      `Not Available Reason '${this.selectedOption.reasonId}: ${this.selectedOption.name}' selected for request ${this.selectedRequest.requestId}`,
    );

    this.isProcessingNar = true;
    this.narService
      .sendNotAvailableReason(this.selectedRequest.requestId, this.selectedOption.reasonId, null)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.isProcessingNar = false;
        if (this.openRequests.length > 1) {
          this.openRequests = this.openRequests.slice(1);
          this.selectedRequest = this.openRequests[0];
          this.onClearValueClick();
        } else {
          this.cancel();
        }
      });
  }

  private _filter(value: string): NotAvailableReason[] {
    const filterValue = value.toLowerCase();
    return this.dropdownOptions.filter((option) => option?.name.toLowerCase().includes(filterValue));
  }

  private redirectHome(): void {
    this.routingService.goToDefaultPage();
  }

  private updateDropdown(): void {
    this.dropdownOptions = [];
    const buttonFields = [...this.narButtonFields.nativeElement.children];
    let noOfRows = 0;
    let lastRowOffset = 0;
    buttonFields.forEach((element) => {
      if (element.offsetTop !== lastRowOffset) {
        noOfRows++;
        lastRowOffset = element.offsetTop;
      }
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      if (noOfRows > 2) {
        this.dropdownOptions.push(this.notAvailableReasons.find((x) => x.reasonId === element.id));
      }
    });

    this.filteredOptions = of(this.dropdownOptions);
    this.filteredOptions = this.control.valueChanges.pipe(
      startWith(''),
      map((value) => this._filter(value || '')),
    );
  }
}
